/* global bootstrap */

/**
 * Cookie notice: shows the banner while the visitor hasn't chosen (or chose an older version of the
 * notice), keeps the preference toggles in sync with the choice cookie and posts every choice to
 * `salvar-aceite` (components/cookies/salvar-aceite.php), which records it and sets the cookie.
 *
 * The decision happens here, not in PHP, because pages are served from PageCache as the same HTML
 * for everyone. Other scripts can check the choice with `window.cookieConsent.has('analitico')`
 * or listen for the `cookies:aceite` event on `document`.
 * @returns {void}
 */
function initCookieNotice() {
    const banner = document.querySelector('.cookies');
    const modal = document.getElementById('modal-cookies');

    if (!banner || !modal) {
        return;
    }

    const form = modal.querySelector('form.salvar-cookies');
    const toggles = form.querySelectorAll('input[type="checkbox"][name="cookies_aceitos[]"]');
    const cookieName = banner.dataset.cookie;
    const version = banner.dataset.versao;

    /**
     * Reads the visitor's current choice from the cookie set by salvar-aceite.php.
     * @returns {{id: number, versao: string, cookies: string[]}|null} The choice, or null when there's none.
     */
    function readChoice() {
        const entry = document.cookie.split('; ').find(item => item.startsWith(cookieName + '='));

        if (!entry) {
            return null;
        }
        try {
            const choice = JSON.parse(decodeURIComponent(entry.slice(cookieName.length + 1)));
            return choice && Array.isArray(choice.cookies) ? choice : null;
        } catch {
            return null;
        }
    }

    /**
     * Shows the banner when there's no valid choice for the current version, and checks the toggles
     * of the accepted categories.
     * @param {{versao: string, cookies: string[]}|null} choice Current choice.
     * @returns {void}
     */
    function applyChoice(choice) {
        const accepted = choice ? choice.cookies : [];

        banner.classList.toggle('is-visible', !choice || choice.versao !== version);
        toggles.forEach(toggle => {
            toggle.checked = accepted.includes(toggle.value);
        });
    }

    /**
     * Posts the chosen categories, then hides the banner and notifies other scripts.
     * @param {string[]} categories Accepted categories ("necessario" is always added by the server).
     * @returns {void}
     */
    function saveChoice(categories) {
        const data = new FormData();
        const focusInBanner = banner.contains(document.activeElement);

        categories.forEach(category => data.append('cookies_aceitos[]', category));

        fetch(form.action, {
            method: 'POST',
            body: data,
            headers: { Accept: 'application/json' }
        })
            .then(response => response.json())
            .then(body => {
                if (!body.success) {
                    throw new Error(body.message);
                }
                applyChoice(body.aceite);
                if (focusInBanner) {
                    document.querySelector('.cookies-rever button')?.focus();
                }
                document.dispatchEvent(new CustomEvent('cookies:aceite', { detail: body.aceite }));
                if (window.dataLayer) {
                    window.dataLayer.push({ event: 'cookies_aceite', cookies_aceitos: body.aceite.cookies });
                }
            })
            .catch(error => {
                console.error('Não foi possível salvar as preferências de cookies.', error);
            });
    }

    const allCategories = Array.from(toggles, toggle => toggle.value);

    document.querySelectorAll('.cookies .btn-aceita, #modal-cookies .btn-aceita').forEach(button => {
        button.addEventListener('click', () => saveChoice(allCategories));
    });
    document.querySelectorAll('.cookies .btn-rejeita, #modal-cookies .btn-rejeita').forEach(button => {
        button.addEventListener('click', () => saveChoice([]));
    });

    form.addEventListener('submit', event => {
        event.preventDefault();
        saveChoice(Array.from(toggles).filter(toggle => toggle.checked).map(toggle => toggle.value));
        bootstrap.Modal.getOrCreateInstance(modal).hide();
    });

    // Toggles changed and then the modal closed without confirming: reopen it showing the saved choice.
    modal.addEventListener('show.bs.modal', () => applyChoice(readChoice()));

    window.cookieConsent = {
        /**
         * Tells whether the visitor accepted a cookie category in the current version of the notice.
         * @param {string} category "necessario", "funcional" or "analitico".
         * @returns {boolean} True when accepted.
         */
        has(category) {
            const choice = readChoice();
            return Boolean(choice && choice.versao === version && choice.cookies.includes(category));
        }
    };

    applyChoice(readChoice());
}

initCookieNotice();
